import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaClock } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "critical") return "#dc2626";
    if ($type === "warning") return "#f59e0b";
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "critical") return "rgba(220, 38, 38, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "critical") return "#dc2626";
      if ($type === "warning") return "#f59e0b";
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const PhaseBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .phase-time {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.iconColor};
    margin-bottom: 1rem;
    font-weight: 600;
  }

  ol {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      line-height: 1.6;
    }
  }
`;

const TemplateBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) =>
    theme.isdarkmode ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.04)"};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }

  p {
    margin: 0.5rem 0;
    line-height: 1.7;
  }

  .placeholder {
    font-style: italic;
    opacity: 0.75;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DataBreachResponseGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.DATA_BREACH_RESPONSE);

  const heroImageSrc = "/images/guides/data-breach-response.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Did Our Data Get Stolen? Quick Breach Response"
        seoTitle="Data Breach Response Guide: First 24-48 Hours | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Suspect a data breach? Follow this quick guide to identify the breach, contain it, preserve evidence, and meet notification requirements."
      ogTitle="Data Breach Response Guide: First 24-48 Hours | CSFSafe"
      ogDescription="A 24-48 hour action plan for suspected data breaches, plus notification templates and compliance basics for SMBs."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Data breach response and notification"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        Suspecting that customer, employee, or business data has been stolen
        is one of the most stressful moments a small business can face. What
        you do in the first 24–48 hours shapes your legal exposure, your
        customers' trust, and how quickly you recover. This guide walks
        through identification, containment, notification, and documentation.
      </Paragraph>

      <H2>Identifying a Breach</H2>

      <CalloutBox $type="critical">
        <strong>Signs You May Have a Data Breach</strong>
        <ul>
          <li>Unusual outbound data transfers or spikes in network traffic</li>
          <li>Alerts from security tools about data exfiltration</li>
          <li>A ransomware note claiming to have copied your data</li>
          <li>Reports of your customer data appearing on the dark web or for sale</li>
          <li>Unauthorized access to databases, file shares, or cloud storage</li>
          <li>A vendor or partner notifies you that your shared data was exposed</li>
          <li>Unexplained account activity or new admin accounts</li>
        </ul>
      </CalloutBox>

      <Paragraph>
        Not every security incident is a data breach. A breach specifically
        involves unauthorized access to, or acquisition of, sensitive data
        (personal information, financial records, health data, trade secrets,
        etc.). Determining whether data was actually accessed or copied—not
        just whether a system was compromised—is the first critical question.
      </Paragraph>

      <H2>Immediate Containment (First 24 Hours)</H2>

      <PhaseBox>
        <h3>Stop the Bleeding</h3>
        <div className="phase-time">
          <FaClock /> 0–24 hours
        </div>
        <ol>
          <li>
            <strong>Isolate affected systems:</strong> Disconnect compromised
            servers, databases, or accounts from the network without shutting
            them down (this preserves evidence).
          </li>
          <li>
            <strong>Assemble your response team:</strong> IT/security lead,
            executive sponsor, legal counsel, and communications lead. Small
            organizations should identify these people now if not already
            named.
          </li>
          <li>
            <strong>Preserve evidence before making changes:</strong>{" "}
            Screenshot logs, save copies of relevant system states, and note
            exact timestamps of what you observe.
          </li>
          <li>
            <strong>Determine what data was involved:</strong> Names, SSNs,
            payment card data, health records, login credentials? The type of
            data determines your legal obligations.
          </li>
          <li>
            <strong>Change credentials for affected and adjacent systems:</strong>{" "}
            Reset passwords and revoke active sessions/API tokens from a clean
            device.
          </li>
          <li>
            <strong>Contact legal counsel immediately:</strong> Breach
            notification laws vary significantly by state and data type—legal
            guidance early prevents costly missteps.
          </li>
          <li>
            <strong>Contact your cyber insurance carrier:</strong> Many
            policies require early notification and can connect you with
            approved forensic and legal resources.
          </li>
        </ol>
      </PhaseBox>

      <H3>24–48 Hour Action Plan</H3>

      <PhaseBox>
        <h3>Assess Scope and Prepare to Notify</h3>
        <div className="phase-time">
          <FaClock /> 24–48 hours
        </div>
        <ol>
          <li>
            <strong>Engage forensic investigators:</strong> Determine exactly
            what was accessed, when, how, and by whom (if identifiable).
          </li>
          <li>
            <strong>Identify affected individuals or organizations:</strong>{" "}
            Build a list of whose data was involved—this drives notification
            scope.
          </li>
          <li>
            <strong>Review applicable laws and contracts:</strong> State
            breach notification laws, industry regulations (HIPAA, GLBA,
            PCI-DSS), and contractual obligations to customers or vendors.
          </li>
          <li>
            <strong>Draft notification communications:</strong> Prepare
            customer, employee, regulator, and (if needed) public statements
            with legal review before sending.
          </li>
          <li>
            <strong>Set up support resources:</strong> Credit monitoring,
            dedicated phone line, or FAQ page for affected individuals if the
            breach involves personal data.
          </li>
          <li>
            <strong>Document every decision and action taken:</strong> This
            record matters for regulators, insurers, and any future legal
            proceedings.
          </li>
        </ol>
      </PhaseBox>

      <H2>Notification Requirements</H2>

      <CalloutBox $type="warning">
        <strong>Know Your Obligations</strong>
        <ul>
          <li>
            <strong>State laws:</strong> All 50 U.S. states have data breach
            notification laws with different triggers and deadlines (often
            30–60 days, some as short as 72 hours for certain industries)
          </li>
          <li>
            <strong>Industry regulations:</strong> HIPAA (healthcare), GLBA
            (financial services), PCI-DSS (payment cards) each have their own
            notification timelines and requirements
          </li>
          <li>
            <strong>Contractual obligations:</strong> Vendor and customer
            contracts often specify notification deadlines that can be shorter
            than legal minimums
          </li>
          <li>
            <strong>International data:</strong> GDPR requires notification to
            regulators within 72 hours of becoming aware, if you handle EU
            resident data
          </li>
          <li>
            <strong>Insurance requirements:</strong> Many cyber policies
            require notifying the carrier before you notify anyone else
          </li>
        </ul>
      </CalloutBox>

      <H2>Documentation & Evidence Preservation</H2>

      <ChecklistSection>
        <h3>What to Document</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            Timeline: when the breach occurred, when it was discovered, when
            each response action was taken
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            System logs, access logs, and network traffic data from before and
            during the incident
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            Exact data types and record counts affected
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Copies of any ransom notes, attacker communications, or evidence
            of data for sale
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            All internal decisions and who made them, with timestamps
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            Copies of all external communications: legal advice, insurer
            correspondence, forensic reports
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Remediation steps taken and verification they were effective
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Stakeholder Communication</H2>

      <PhaseBox>
        <h3>Who to Tell, and In What Order</h3>
        <div className="phase-time">
          <FaClock /> Sequenced, legal-guided
        </div>
        <ol>
          <li>
            <strong>Leadership and board:</strong> Brief immediately, before
            any external communication
          </li>
          <li>
            <strong>Legal counsel and insurer:</strong> Engage before drafting
            any notification
          </li>
          <li>
            <strong>Regulators:</strong> Notify per applicable law's timeline,
            with legal counsel's guidance on exact wording
          </li>
          <li>
            <strong>Affected individuals:</strong> Clear, plain-language
            notice explaining what happened, what data was involved, and what
            they should do
          </li>
          <li>
            <strong>Employees:</strong> Internal briefing so staff can answer
            basic questions consistently and know not to speculate publicly
          </li>
          <li>
            <strong>Media/public (if applicable):</strong> A prepared
            statement for significant breaches, coordinated with legal and
            communications counsel
          </li>
        </ol>
      </PhaseBox>

      <H2>Breach Notification Template</H2>

      <TemplateBox>
        <h3>Customer/Individual Notification Letter (Starting Point)</h3>
        <p>
          <strong>Subject:</strong> Important Notice About Your Data
        </p>
        <p className="placeholder">
          Dear [Name],
        </p>
        <p>
          We are writing to inform you of a data security incident that may
          have affected your personal information. On [date], we discovered
          that [brief description of what happened]. Our investigation
          determined that the following information may have been involved:
          [specific data types].
        </p>
        <p>
          We took immediate action to contain the incident, including
          [containment actions]. We have also [notified law
          enforcement/engaged forensic investigators/etc.].
        </p>
        <p>
          We recommend you [specific protective steps: monitor accounts,
          place a fraud alert, change passwords, etc.]. We are offering
          [credit monitoring/identity protection services, if applicable] at
          no cost to you.
        </p>
        <p>
          If you have questions, please contact us at [phone/email/dedicated
          line]. We take the security of your information seriously and
          apologize for this incident.
        </p>
        <p className="placeholder">
          Sincerely, [Name/Title]
        </p>
      </TemplateBox>

      <H2>Compliance & Legal Considerations for SMBs</H2>

      <CalloutBox $type="tip">
        <strong>Don't Navigate This Alone</strong>
        <ul>
          <li>
            Retain outside legal counsel with data breach experience—generic
            business attorneys may not know current notification requirements
          </li>
          <li>
            If you accept payment cards, review PCI-DSS obligations, which may
            require notifying your payment processor and card brands
          </li>
          <li>
            If you handle health information, HIPAA breach notification rules
            apply strict timelines (60 days to individuals, sooner to HHS for
            large breaches)
          </li>
          <li>
            Multi-state notification is common—affected individuals in
            different states may trigger different deadlines and requirements
          </li>
          <li>
            Keep insurer-approved vendors (forensics, legal, PR) on a
            pre-identified list before an incident happens, since policies
            often require using approved providers
          </li>
        </ul>
      </CalloutBox>

      <H2>What NOT to Do</H2>

      <CalloutBox $type="dont">
        <strong>Avoid These Mistakes</strong>
        <ul>
          <li>Don't delay notification while you "figure everything out"—most laws require timely action even with incomplete information</li>
          <li>Don't speculate publicly about cause or scope before facts are confirmed</li>
          <li>Don't wipe or rebuild systems before forensic evidence is captured</li>
          <li>Don't notify only some affected individuals while investigation continues—plan for phased or supplemental notices if needed</li>
          <li>Don't skip legal review of notification language, even under time pressure</li>
        </ul>
      </CalloutBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.ARTICLE.INCIDENT_RESPONSE_PLAN)}>
            Incident Response Plans: Why Every Organization Needs One
          </a>
        </li>
        <li>
          <a
            href={href(
              LINKS.CYBERSECURITY_FRAMEWORK.DATA_BREACH_RESPONSE_AND_COMPLIANCE
            )}
          >
            Data Breach Response and Compliance
          </a>
        </li>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.INCIDENT_RESPONSE)}>
            Quick Incident Response Checklist
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.DATA_EXFILTRATION)}>
            Data Exfiltration Threat Type
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.DATA_CLASSIFICATION)}>
            Data Classification Article
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/data-breach-response"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

DataBreachResponseGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default DataBreachResponseGuide;
